import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Star, ClipboardCheck, CheckCircle2, Calendar, Hash, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          data-testid={`star-${s}`}
          className="transition"
        >
          <Star
            className="h-7 w-7"
            style={{
              fill: s <= value ? '#fbbf24' : 'transparent',
              color: s <= value ? '#fbbf24' : '#475569',
            }}
          />
        </button>
      ))}
    </div>
  );
}

export default function ClientReviewsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('pending');
  const [open, setOpen] = useState(null); // currently reviewing entry
  const [rating, setRating] = useState(5);
  const [note, setNote] = useState('');

  const load = async () => {
    const [pending, all] = await Promise.all([
      api.get('/daily-reports/pending-review').catch(() => ({ data: [] })),
      api.get('/daily-reports').catch(() => ({ data: [] })),
    ]);
    setItems(pending.data);
    setHistory((all.data || []).filter((r) => r.client_review_rating));
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    await api.post(`/daily-reports/${open.id}/review`, { rating, note });
    setOpen(null); setRating(5); setNote('');
    load();
  };

  const list = tab === 'pending' ? items : history;

  // "My Work Report" — Client-portal download. Scopes to this client user's own
  // company server-side (never trusts the query), hours are always stripped.
  const downloadMyWorkReport = async (fmt = 'pdf') => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const start = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
      const token = localStorage.getItem('shine_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/client-portal/work-report?date_from=${start}&date_to=${today}&format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const t = await res.text();
        toast.error('Download failed: ' + t);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_work_report_${start}_to_${today}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error('Download error: ' + e.message);
    }
  };

  return (
    <div>
      <PageHeader
        icon={ClipboardCheck}
        title="Reviews"
        subtitle="Rate and approve work submitted by the SHINE team."
        actions={
          <Button
            onClick={() => downloadMyWorkReport('pdf')}
            variant="outline"
            className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 font-semibold"
            data-testid="client-portal-download-btn"
            title="Download this month's work (counts & status only — no hours)"
          >
            <Download className="h-4 w-4 mr-1" /> My Work Report
          </Button>
        }
      />

      <Card className="shine-card p-3 rounded-xl mb-4 flex items-center gap-2">
        <Button size="sm" variant={tab === 'pending' ? 'default' : 'outline'} onClick={() => setTab('pending')}
          className={tab === 'pending' ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'}
          data-testid="tab-pending">
          Pending review ({items.length})
        </Button>
        <Button size="sm" variant={tab === 'history' ? 'default' : 'outline'} onClick={() => setTab('history')}
          className={tab === 'history' ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'}
          data-testid="tab-history">
          Past reviews ({history.length})
        </Button>
      </Card>

      {list.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          {tab === 'pending' ? 'Nothing waiting for your review right now.' : 'No past reviews yet.'}
        </Card>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <Card key={r.id} className="shine-card p-4 rounded-xl" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{r.format}</Badge>
                    <Badge variant="outline" className="border-sky-500/40 text-sky-300 text-[10px]">{r.status}</Badge>
                    {r.client_review_rating && (
                      <span className="inline-flex items-center text-xs text-amber-200">
                        {'★'.repeat(r.client_review_rating)}{'☆'.repeat(5 - r.client_review_rating)}
                      </span>
                    )}
                  </div>
                  <h4 className="font-bold text-white">{r.item_description}</h4>
                  <div className="mt-1 text-xs text-slate-400 flex flex-wrap items-center gap-x-3">
                    <span>by {r.user_name} · {r.user_role}</span>
                    {r.project_name && <span>· {r.project_name}</span>}
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(r.date).toLocaleDateString()}</span>
                    <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> {r.count}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {r.hours}h</span>
                  </div>
                  {r.client_review_note && (
                    <div className="mt-2 text-xs text-amber-100/90 bg-amber-500/5 border border-amber-500/15 rounded-md px-2 py-1">
                      “{r.client_review_note}”
                    </div>
                  )}
                </div>
                {tab === 'pending' && (
                  <Button onClick={() => { setOpen(r); setRating(5); setNote(''); }}
                    className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid={`btn-review-${r.id}`}>
                    <Star className="h-4 w-4 mr-1" /> Review
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          {open && (
            <>
              <DialogHeader><DialogTitle className="text-white">Review work</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-white">{open.item_description}</div>
                  <div className="text-xs text-slate-400 mt-1">by {open.user_name} · {open.format} · {new Date(open.date).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Your rating</div>
                  <Stars value={rating} onChange={setRating} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Note (optional)</div>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="bg-[#0f2240] border-amber-500/20" placeholder="Quick feedback for the team…" data-testid="review-note" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="review-submit">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Submit review
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
